<template>
  <div class="case-details-modal">
    <!-- Case Header - Design System V3 -->
    <div class="bg-slate-50/80 backdrop-blur-xl -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-2xl">
      <div class="flex items-start justify-between">
        <div>
          <h3 class="text-lg font-medium text-slate-900">
            Caso #{{ caseData._id.slice(-8).toUpperCase() }}
          </h3>
          <div class="mt-1 flex items-center space-x-4 text-sm text-slate-600">
            <span>{{ caseData.user?.firstName }} {{ caseData.user?.lastName }}</span>
            <span>{{ caseData.user?.email }}</span>
            <StatusBadge :status="caseData.status" size="sm" />
          </div>
        </div>
        <div class="text-right">
          <div class="text-sm text-slate-600">
            Creado: {{ formatDate(caseData.createdAt) }}
          </div>
          <div v-if="caseData.updatedAt !== caseData.createdAt" class="text-sm text-slate-600">
            Actualizado: {{ formatDate(caseData.updatedAt) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Case Information -->
    <div class="space-y-6">
      <!-- Basic Info -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Información del caso</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt class="text-sm font-medium text-slate-500">Plan de servicio</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ formatServiceTier(caseData.serviceTier) }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Servicios solicitados</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ formatServices(caseData.services) }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Urgencia</dt>
              <dd class="mt-1 text-sm text-slate-900">
                <span :class="{
                  'text-red-600': caseData.urgency === 'high',
                  'text-yellow-600': caseData.urgency === 'medium',
                  'text-green-600': caseData.urgency === 'low'
                }">
                  {{ formatUrgency(caseData.urgency) }}
                </span>
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Días abierto</dt>
              <dd class="mt-1 text-sm text-slate-900">
                {{ getDaysOpen(caseData.createdAt) }} días
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Description -->
      <div v-if="caseData.description">
        <h4 class="text-sm font-medium text-slate-900 mb-3">Descripción del caso</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <p class="text-sm text-slate-700 whitespace-pre-wrap">{{ caseData.description }}</p>
        </div>
      </div>

      <!-- URLs Section (for olvido and olvido_postmortem cases) -->
      <div v-if="hasUrls && caseData.urls?.length">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-slate-900">URLs a gestionar</h4>
          <span v-if="urlsProgress" class="text-sm font-medium text-slate-600">
            {{ urlsProgress.resolved }}/{{ urlsProgress.total }} resueltas
          </span>
        </div>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <!-- Progress Bar -->
          <div v-if="urlsProgress" class="mb-4">
            <div class="flex items-center justify-between text-sm mb-2">
              <span class="text-slate-600">Progreso de gestión</span>
              <span class="font-medium text-slate-900">{{ urlsProgress.percentage }}%</span>
            </div>
            <div class="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
                :style="{ width: `${urlsProgress.percentage}%` }"
              ></div>
            </div>
          </div>

          <!-- URLs List -->
          <div class="space-y-3">
            <div
              v-for="(urlItem, index) in caseData.urls"
              :key="index"
              :class="[
                'p-3 rounded-lg border transition-all duration-200',
                getUrlStatusConfig(urlItem.status).bgClass,
                getUrlStatusConfig(urlItem.status).borderClass
              ]"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="flex-1 min-w-0">
                  <!-- URL Link -->
                  <a
                    :href="sanitizeUrl(urlItem.url)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-2 text-sm text-slate-900 hover:text-teal-600 transition-colors group"
                  >
                    <svg class="w-4 h-4 flex-shrink-0 text-slate-400 group-hover:text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span class="font-medium truncate">{{ urlItem.url }}</span>
                    <svg class="w-3 h-3 flex-shrink-0 text-slate-400 group-hover:text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>

                  <!-- Description -->
                  <p v-if="urlItem.description" class="mt-1 text-xs text-slate-600">
                    {{ urlItem.description }}
                  </p>

                  <!-- Dates -->
                  <div v-if="urlItem.dateContacted || urlItem.dateResolved" class="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                    <span v-if="urlItem.dateContacted">
                      Contactado: {{ formatDateShort(urlItem.dateContacted) }}
                    </span>
                    <span v-if="urlItem.dateResolved">
                      Resuelto: {{ formatDateShort(urlItem.dateResolved) }}
                    </span>
                  </div>

                  <!-- URL Action Buttons -->
                  <div class="mt-2 flex flex-wrap gap-2">
                    <button
                      v-if="urlItem.status === 'pending'"
                      @click="$emit('url-status-change', caseData, urlItem, 'contacted')"
                      class="url-action-btn url-action-btn--contacted"
                    >
                      Marcar Contactado
                    </button>
                    <button
                      v-if="urlItem.status === 'contacted'"
                      @click="$emit('url-status-change', caseData, urlItem, 'resolved')"
                      class="url-action-btn url-action-btn--resolved"
                    >
                      Marcar Resuelto
                    </button>
                    <button
                      v-if="urlItem.status === 'resolved'"
                      @click="$emit('url-status-change', caseData, urlItem, 'contacted')"
                      class="url-action-btn url-action-btn--contacted"
                    >
                      Volver a Contactado
                    </button>
                    <button
                      v-if="urlItem.status !== 'pending'"
                      @click="$emit('url-status-change', caseData, urlItem, 'pending')"
                      class="url-action-btn url-action-btn--pending"
                    >
                      Volver a pendiente
                    </button>
                  </div>
                </div>

                <!-- Status Badge -->
                <span
                  :class="[
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                    getUrlStatusConfig(urlItem.status).bgClass,
                    getUrlStatusConfig(urlItem.status).textClass
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full', getUrlStatusConfig(urlItem.status).dotClass]"></span>
                  {{ getUrlStatusConfig(urlItem.status).label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- User Information -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Información del cliente</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt class="text-sm font-medium text-slate-500">Nombre completo</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ caseData.user?.firstName }} {{ caseData.user?.lastName }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Email</dt>
              <dd class="mt-1 text-sm text-slate-900 flex items-center gap-2">
                {{ caseData.user?.email }}
                <span
                  v-if="caseData.user?.isEmailVerified"
                  class="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full"
                >
                  <CheckCircleIcon class="w-3 h-3" />
                  Verificado
                </span>
              </dd>
            </div>
            <div v-if="caseData.user?.phone">
              <dt class="text-sm font-medium text-slate-500">Teléfono</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ caseData.user?.phone }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">DNI</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ maskDni(caseData.user?.dni) }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Jurisdicción</dt>
              <dd class="mt-1 text-sm text-slate-900">{{ formatAutonomousCommunity(caseData.user?.address?.autonomousCommunity) }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Account Information -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Información de cuenta</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt class="text-sm font-medium text-slate-500">Estado cuenta</dt>
              <dd class="mt-1">
                <span :class="caseData.user?.isActive ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800' : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'">
                  {{ caseData.user?.isActive ? 'Activa' : 'Inactiva' }}
                </span>
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Estado pago</dt>
              <dd class="mt-1">
                <span :class="getPaymentStatusClass(caseData.user?.paymentStatus)">
                  {{ formatPaymentStatus(caseData.user?.paymentStatus) }}
                </span>
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Último login</dt>
              <dd class="mt-1 text-sm text-slate-900">
                {{ caseData.user?.lastLogin ? formatDate(caseData.user.lastLogin) : 'Nunca' }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-slate-500">Miembro desde</dt>
              <dd class="mt-1 text-sm text-slate-900">
                {{ caseData.user?.createdAt ? formatDateShort(caseData.user.createdAt) : 'N/A' }}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Required Documents Section -->
      <div v-if="caseData.requiredDocuments">
        <h4 class="text-sm font-medium text-slate-900 mb-3">Documentos obligatorios</h4>
        <div class="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200">
          <!-- DNI -->
          <div class="p-4 hover:bg-accent-50/30 transition-colors duration-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <IdentificationIcon class="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900">DNI / NIE</p>
                  <p class="text-sm" :class="getRequiredDocStatusClass(caseData.requiredDocuments?.dni)">
                    {{ getRequiredDocStatusText(caseData.requiredDocuments?.dni) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <Button
                  v-if="caseData.requiredDocuments?.dni?.documentId"
                  @click="downloadRequiredDocument('dni')"
                  size="sm"
                  variant="outline"
                >
                  Ver
                </Button>
              </div>
            </div>
          </div>

          <!-- Authorization -->
          <div v-if="caseData.type !== 'testamento'" class="p-4 hover:bg-accent-50/30 transition-colors duration-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <DocumentCheckIcon class="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900">Autorización firmada</p>
                  <p class="text-sm" :class="getRequiredDocStatusClass(caseData.requiredDocuments?.authorization)">
                    {{ getRequiredDocStatusText(caseData.requiredDocuments?.authorization) }}
                    <span v-if="caseData.requiredDocuments?.authorization?.signatureType === 'digital'" class="text-teal-600">
                      (Firma digital)
                    </span>
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <Button
                  v-if="caseData.requiredDocuments?.authorization?.documentId"
                  @click="downloadRequiredDocument('authorization')"
                  size="sm"
                  variant="outline"
                >
                  Ver
                </Button>
              </div>
            </div>
          </div>

          <!-- Legitimation-specific documents (dynamic per legalCapacity) -->
          <div
            v-for="doc in legitimationDocs"
            :key="doc.key"
            class="p-4 hover:bg-accent-50/30 transition-colors duration-200"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <DocumentTextIcon class="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900">{{ doc.label }}</p>
                  <p class="text-sm" :class="getRequiredDocStatusClass(caseData.requiredDocuments?.[doc.key])">
                    {{ getRequiredDocStatusText(caseData.requiredDocuments?.[doc.key]) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <Button
                  v-if="caseData.requiredDocuments?.[doc.key]?.documentId"
                  @click="downloadRequiredDocument(doc.key)"
                  size="sm"
                  variant="outline"
                >
                  Ver
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Documents Section -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Documentos adicionales</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <!-- Already requested additional docs -->
          <div v-if="additionalDocs.length > 0" class="mb-4">
            <div class="space-y-2">
              <div
                v-for="addDoc in additionalDocs"
                :key="addDoc._id || addDoc.id"
                class="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
              >
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-slate-900">{{ addDoc.name }}</p>
                  <p v-if="addDoc.reason" class="text-xs text-slate-500 mt-0.5">{{ addDoc.reason }}</p>
                  <p v-if="addDoc.rejectionReason" class="text-xs text-red-500 mt-0.5">
                    Motivo: {{ addDoc.rejectionReason }}
                  </p>
                </div>
                <span :class="getAdditionalDocStatusClass(addDoc.status)">
                  {{ formatAdditionalDocStatus(addDoc.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Request new additional document form -->
          <div class="bg-slate-50 rounded-xl p-4">
            <h5 class="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
              <PlusCircleIcon class="w-4 h-4 text-slate-600" />
              Solicitar documento adicional
            </h5>
            <div class="space-y-3">
              <div>
                <label for="caseDetailAddDocName" class="block text-xs font-medium text-slate-700 mb-1">
                  Nombre del documento <span class="text-red-500">*</span>
                </label>
                <input
                  id="caseDetailAddDocName"
                  v-model="newDocName"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 focus:outline-none transition-all duration-200"
                  placeholder="Ej: Certificado de empadronamiento"
                />
              </div>
              <div>
                <label for="caseDetailAddDocReason" class="block text-xs font-medium text-slate-700 mb-1">
                  Motivo de la solicitud <span class="text-red-500">*</span>
                </label>
                <textarea
                  id="caseDetailAddDocReason"
                  v-model="newDocReason"
                  rows="2"
                  class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 focus:outline-none resize-none transition-all duration-200"
                  placeholder="Explica por qué se necesita este documento (mín. 10 caracteres)..."
                ></textarea>
              </div>
              <button
                type="button"
                :disabled="!canRequestDoc || requestingDoc"
                class="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                @click="requestAdditionalDoc"
              >
                <PlusCircleIcon class="w-4 h-4" />
                {{ requestingDoc ? 'Solicitando...' : 'Solicitar documento' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-slate-900">Documentos ({{ caseData.documents?.length || 0 }})</h4>
          <Button
            @click="$emit('upload-document', caseData)"
            size="sm"
            variant="outline"
          >
            + Subir Documento
          </Button>
        </div>
        <div class="bg-white border border-slate-200 rounded-xl">
          <div v-if="!caseData.documents?.length" class="p-8 text-center text-slate-500">
            <DocumentIcon class="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <p class="text-sm">No hay documentos adjuntos</p>
          </div>
          <div v-else class="divide-y divide-slate-200">
            <div
              v-for="doc in caseData.documents"
              :key="doc._id"
              class="p-4 hover:bg-accent-50/30 transition-colors duration-200"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="flex-shrink-0">
                    <DocumentIcon class="w-8 h-8 text-slate-400" />
                  </div>
                  <div>
                    <p class="text-sm font-medium text-slate-900">{{ doc.originalName }}</p>
                    <p class="text-sm text-slate-500">{{ formatDocType(doc.type) }}</p>
                    <p class="text-xs text-slate-400">
                      Subido {{ formatDate(doc.uploadedAt) }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <Button
                    @click="downloadDocument(doc)"
                    size="sm"
                    variant="outline"
                  >
                    Descargar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline V3 -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Timeline del caso</h4>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <div v-if="!caseData.timeline?.length" class="text-center text-slate-500 py-4">
            <p class="text-sm">No hay eventos en el timeline</p>
          </div>
          <div v-else class="timeline-v3">
            <div
              v-for="(event, index) in caseData.timeline"
              :key="event._id"
              class="timeline-item"
            >
              <div class="timeline-line" :class="{ 'timeline-line--last': index === caseData.timeline.length - 1 }">
                <div class="timeline-dot" :class="getTimelineDotClass(event)">
                  <EnvelopeIcon v-if="event.action === 'client_contact'" class="w-4 h-4" />
                  <CheckCircleIcon v-else-if="event.type === 'completed'" class="w-4 h-4" />
                  <ClockIcon v-else-if="event.type === 'in_progress'" class="w-4 h-4" />
                  <DocumentIcon v-else class="w-4 h-4" />
                </div>
              </div>
              <div class="timeline-content">
                <p class="timeline-title">{{ event.description }}</p>
                <p class="timeline-date">
                  {{ formatDate(event.date) }}
                  <span v-if="event.createdBy" class="text-slate-400"> · {{ event.createdBy }}</span>
                </p>
                <div v-if="event.metadata?.notes" class="timeline-description">
                  "{{ event.metadata.notes }}"
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Status Actions -->
      <div>
        <h4 class="text-sm font-medium text-slate-900 mb-3">Acciones</h4>
        <div class="flex flex-wrap gap-3">
          <Button
            v-if="canChangeStatus('in_progress')"
            @click="$emit('status-change', caseData, 'in_progress')"
            variant="outline"
            size="sm"
          >
            Marcar en Proceso
          </Button>
          <Button
            v-if="canChangeStatus('completed')"
            @click="$emit('status-change', caseData, 'completed')"
            variant="primary"
            size="sm"
          >
            Marcar Completado
          </Button>
          <Button
            v-if="caseData.status !== 'pending'"
            @click="$emit('status-change', caseData, 'pending')"
            variant="outline"
            size="sm"
          >
            Volver a pendiente
          </Button>
          <Button
            @click="sendEmailToClient"
            variant="outline"
            size="sm"
          >
            Enviar email al cliente
          </Button>
        </div>
      </div>
    </div>

    <!-- Modal Footer - Design System V3 -->
    <div class="border-t border-slate-200 -mx-6 -mb-6 px-6 py-4 mt-6 bg-slate-50/50 backdrop-blur-sm">
      <div class="flex justify-end">
        <Button
          @click="$emit('close')"
          variant="outline"
        >
          Cerrar
        </Button>
      </div>
    </div>

    <SendEmailModal
      :show="showSendEmailModal"
      :recipient-name="(caseData.user?.firstName || '') + ' ' + (caseData.user?.lastName || '')"
      :recipient-email="caseData.user?.email || ''"
      :initial-subject="emailSubjectPrefix"
      context="case"
      :case-id="caseData._id"
      @close="showSendEmailModal = false"
      @sent="handleEmailSent"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { DocumentIcon, CheckCircleIcon, ClockIcon, IdentificationIcon, DocumentCheckIcon, DocumentTextIcon, PlusCircleIcon, EnvelopeIcon } from '@heroicons/vue/24/outline'
import StatusBadge from '@/components/ui/StatusBadge.vue'
import Button from '@/components/ui/Button.vue'
import SendEmailModal from '@/components/admin/SendEmailModal.vue'
import { useAdditionalDocuments } from '@/composables/useAdditionalDocuments'
import { useCaseFormatters } from '@/composables/useCaseFormatters'
import { useSanitizeHtml } from '@/composables/useSanitizeHtml'
import { ADMIN_REQUIRED_DOCS } from '@/constants/documents'

// Props
const props = defineProps({
  case: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['close', 'status-change', 'upload-document', 'url-status-change', 'download-document', 'additional-doc-requested'])

// Composables
const { formatServiceTier, formatDocType } = useCaseFormatters()
const { sanitizeUrl } = useSanitizeHtml()

// Computed para acceder a la prop 'case' con un nombre válido en JS
const caseData = computed(() => props.case)

// Additional documents (shared composable)
const additionalDocs = computed(() => caseData.value?.additionalRequiredDocuments || [])
const {
  newDocName,
  newDocReason,
  requestingDoc,
  canRequestDoc,
  getAdditionalDocStatusClass,
  formatAdditionalDocStatus,
  requestAdditionalDoc
} = useAdditionalDocuments(
  computed(() => caseData.value?._id),
  { onSuccess: () => emit('additional-doc-requested') }
)

// Documentos específicos por tipo de legitimación (excluye dni y authorization que tienen secciones propias)
const legitimationDocs = computed(() => {
  if (caseData.value?.type !== 'olvido_postmortem') return []
  const legalCapacity = caseData.value?.applicantInfo?.legalCapacity
  const docs = ADMIN_REQUIRED_DOCS[legalCapacity] || []
  return docs.filter(doc => doc.key !== 'dni' && doc.key !== 'authorization')
})

// URLs - Solo para servicios olvido y olvido_postmortem
const hasUrls = computed(() => {
  return ['olvido', 'olvido_postmortem'].includes(caseData.value?.type)
})

const urlsProgress = computed(() => {
  if (!caseData.value?.urls?.length) return null
  const total = caseData.value.urls.length
  const resolved = caseData.value.urls.filter(u => u.status === 'resolved').length
  return {
    total,
    resolved,
    percentage: Math.round((resolved / total) * 100)
  }
})

// Helper para estados de URLs
const getUrlStatusConfig = (status) => {
  const config = {
    pending: {
      label: 'Pendiente',
      bgClass: 'bg-amber-50',
      textClass: 'text-amber-700',
      borderClass: 'border-amber-200',
      dotClass: 'bg-amber-500'
    },
    contacted: {
      label: 'Contactado',
      bgClass: 'bg-blue-50',
      textClass: 'text-blue-700',
      borderClass: 'border-blue-200',
      dotClass: 'bg-blue-500'
    },
    resolved: {
      label: 'Resuelto',
      bgClass: 'bg-green-50',
      textClass: 'text-green-700',
      borderClass: 'border-green-200',
      dotClass: 'bg-green-500'
    }
  }
  return config[status] || config.pending
}

// Methods
const formatServices = (services) => {
  if (!services || !services.length) return 'Sin servicios'

  const types = {
    'derecho-olvido': 'Derecho al Olvido',
    'derecho-olvido-postmortem': 'Derecho al Olvido Post-Mortem',
    'testamento-digital': 'Testamento Digital'
  }

  return services.map(s => types[s] || s).join(', ')
}

const formatUrgency = (urgency) => {
  const urgencies = {
    low: 'Baja',
    medium: 'Media',
    high: 'Alta'
  }
  return urgencies[urgency] || urgency
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateShort = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const maskDni = (dni) => {
  if (!dni) return 'No disponible'
  return '***' + dni.slice(-5)
}

const formatAutonomousCommunity = (community) => {
  const communities = {
    'andalucia': 'Andalucía',
    'aragon': 'Aragón',
    'asturias': 'Asturias',
    'baleares': 'Islas Baleares',
    'canarias': 'Canarias',
    'cantabria': 'Cantabria',
    'castilla-la-mancha': 'Castilla-La Mancha',
    'castilla-y-leon': 'Castilla y León',
    'cataluna': 'Cataluña',
    'comunidad-valenciana': 'Comunidad Valenciana',
    'extremadura': 'Extremadura',
    'galicia': 'Galicia',
    'madrid': 'Comunidad de Madrid',
    'murcia': 'Región de Murcia',
    'navarra': 'Navarra',
    'pais-vasco': 'País Vasco',
    'la-rioja': 'La Rioja',
    'ceuta': 'Ceuta',
    'melilla': 'Melilla'
  }
  return communities[community] || community || 'No especificada'
}

const formatTierName = (tier) => {
  const tiers = {
    olvido: 'Derecho al Olvido',
    olvido_postmortem: 'Post-Mortem',
    testamento: 'Testamento Digital'
  }
  return tiers[tier] || tier || 'N/A'
}

const getTierBadgeClass = (tier) => {
  const classes = {
    olvido: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800',
    olvido_postmortem: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800',
    testamento: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800'
  }
  return classes[tier] || classes.olvido
}

const formatPaymentStatus = (status) => {
  const statuses = {
    pending: 'Pendiente',
    paid: 'Pagado',
    failed: 'Fallido',
    cancelled: 'Cancelado'
  }
  return statuses[status] || status || 'N/A'
}

const getPaymentStatusClass = (status) => {
  const classes = {
    pending: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
    paid: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
    failed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800',
    cancelled: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800'
  }
  return classes[status] || classes.pending
}

const getDaysOpen = (createdAt) => {
  const created = new Date(createdAt)
  const now = new Date()
  const diffTime = Math.abs(now - created)
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const canChangeStatus = (newStatus) => {
  const { status } = props.case
  
  if (newStatus === 'in_progress') {
    return status === 'pending'
  }

  if (newStatus === 'completed') {
    return status === 'in_progress'
  }
  
  return true
}

const downloadDocument = (doc) => {
  const documentId = doc._id || doc.documentId
  if (!documentId) {
    console.error('No document ID found')
    return
  }

  // Emit event to parent for handling download
  emit('download-document', {
    documentId,
    caseId: caseData.value._id,
    originalName: doc.originalName,
    type: doc.type
  })
}

// Status helpers for required documents
const getRequiredDocStatusClass = (doc) => {
  if (!doc?.uploaded) return 'text-amber-600'
  if (doc.status === 'approved') return 'text-green-600'
  if (doc.status === 'rejected') return 'text-red-600'
  return 'text-blue-600'
}

const getRequiredDocStatusText = (doc) => {
  if (!doc?.uploaded) return 'Pendiente de subir'
  if (doc.status === 'approved') return 'Verificado'
  if (doc.status === 'rejected') return 'Rechazado'
  return 'Subido - Pendiente de verificar'
}

const downloadRequiredDocument = (docType) => {
  const documentId = caseData.value?.requiredDocuments?.[docType]?.documentId
  if (!documentId) {
    console.error('No document ID found for', docType)
    return
  }

  const docNames = {
    dni: 'DNI / NIE',
    authorization: 'Autorización firmada',
    deathCertificate: 'Certificado de defunción',
    legitimationDocument: 'Documento de legitimación',
    heirMandate: 'Mandato / Poder notarial'
  }

  emit('download-document', {
    documentId,
    caseId: caseData.value._id,
    type: docType,
    originalName: docNames[docType] || docType,
    preview: true
  })
}

const showSendEmailModal = ref(false)

const emailSubjectPrefix = computed(() => {
  if (!caseData.value?._id) return ''
  return `Caso #${caseData.value._id.slice(-8).toUpperCase()} - `
})

const sendEmailToClient = () => {
  showSendEmailModal.value = true
}

const handleEmailSent = () => {
  showSendEmailModal.value = false
}

// Get timeline dot class based on event type
const getTimelineDotClass = (event) => {
  if (event.type === 'completed' || event.action === 'completed') {
    return 'timeline-dot--completed'
  }
  if (event.type === 'in_progress' || event.action === 'in_progress') {
    return 'timeline-dot--in-progress'
  }
  if (event.action === 'client_contact' || event.metadata?.type === 'admin_email') {
    return 'timeline-dot--email'
  }
  return 'timeline-dot--pending'
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.case-details-modal {
  @apply max-w-4xl mx-auto;
}

/* Document list hover effects */
.document-item:hover {
  @apply bg-accent-50/30 transition-colors duration-200;
}

/* Status badge animations */
.status-badge {
  transition: all 0.2s ease;
}

/* URL Action Buttons */
.url-action-btn {
  @apply px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200;
}

.url-action-btn--contacted {
  @apply bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200;
}

.url-action-btn--resolved {
  @apply bg-green-50 text-green-700 hover:bg-green-100 border border-green-200;
}

.url-action-btn--pending {
  @apply bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200;
}

/* Timeline email dot */
.timeline-dot--email {
  @apply bg-blue-100 text-blue-600 border-blue-400;
}

/* Action buttons responsive layout */
@media (max-width: 640px) {
  .actions-container {
    @apply flex-col space-y-2;
  }

  .actions-container button {
    @apply w-full;
  }
}
</style>
